// components/AddQuestionsModal.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Layer,
  Heading,
  Text,
  Button,
  Spinner,
  CheckBox,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  TextInput,
} from "grommet";
import adminAxios from "../../config/adminAxios";

const AddQuestionsModal = ({
  open,
  onClose,
  portfolioId,
  courseName,
  courseId,
  existingQuestionIds = [],
  onAdded,
}) => {
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    adminAxios
      .get(`/api/admin/questions-by-portfolio/${portfolioId}`)
      .then((res) => setAllQuestions(res.data.questions || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, portfolioId]);

  // Filter out same‐course or already added
  const available = allQuestions.filter(
    (q) =>
      q.course_id !== courseId && !existingQuestionIds.includes(q.question_id),
  );

  // Now apply search
  const filtered = available.filter((q) => {
    const term = searchTerm.trim().toLowerCase();
    return (
      (q.question && q.question.toLowerCase().includes(term)) ||
      (q.course_name && q.course_name.toLowerCase().includes(term))
    );
  });

  const toggleOne = (q) => {
    setSelected((prev) =>
      prev.some((x) => x.question_id === q.question_id)
        ? prev.filter((x) => x.question_id !== q.question_id)
        : [...prev, q],
    );
  };

  const handleConfirm = async () => {
    const payload = {
      portfolio_id: portfolioId,
      course_id: courseId,
      question_ids: selected.map((q) => q.question_id),
    };
    console.log("Adding questions payload:", payload);
    try {
      await adminAxios.post("/api/admin/clone-questions", payload);
      onAdded(selected);
    } catch (err) {
      console.error("Error adding questions:", err);
    }
  };

  if (!open) return null;

  return (
    <Layer onEsc={onClose} onClickOutside={onClose} responsive>
      <Box pad="medium" width="xlarge" background="white" round="small">
        {!showSummary ? (
          <>
            <Heading level={3} margin={{ bottom: "small" }}>
              Select Questions to Add - {courseName}
            </Heading>

            {/* Search bar */}
            <Box margin={{ bottom: "small" }}>
              <TextInput
                placeholder="Search by question or course"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>

            <Box
              border={{ color: "light-4" }}
              pad="xsmall"
              style={{ maxHeight: "50vh", overflowY: "auto" }}
            >
              {loading ? (
                <Box align="center">
                  <Spinner />
                </Box>
              ) : filtered.length === 0 ? (
                <Text>No questions match your search.</Text>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableCell />
                      <TableCell>Question</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Course</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((q) => (
                      <TableRow key={q.question_id}>
                        <TableCell>
                          <CheckBox
                            checked={selected.some(
                              (x) => x.question_id === q.question_id,
                            )}
                            onChange={() => toggleOne(q)}
                          />
                        </TableCell>
                        <TableCell>{q.question}</TableCell>
                        <TableCell>
                          {q.weight === 1 ? "Y/N" : "Range"}
                        </TableCell>
                        <TableCell>{q.course_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
            <Box
              direction="row"
              justify="end"
              gap="small"
              margin={{ top: "small" }}
            >
              <Button label="Cancel" onClick={onClose} />
              <Button
                label="Next"
                primary
                disabled={selected.length === 0}
                onClick={() => setShowSummary(true)}
              />
            </Box>
          </>
        ) : (
          <>
            <Heading level={3} margin={{ bottom: "small" }}>
              Confirm Add Questions
            </Heading>
            <Text>
              You&apos;re adding {selected.length} question
              {selected.length !== 1 ? "s" : ""} to {courseName}.
            </Text>
            <Box
              border={{ color: "light-4" }}
              pad="xsmall"
              margin={{ vertical: "small" }}
              style={{ maxHeight: "30vh", overflowY: "auto" }}
            >
              {selected.map((q) => (
                <Text key={q.question_id} margin={{ bottom: "xsmall" }}>
                  • {q.question} — ({q.weight === 1 ? "Y/N" : "Range"} |{" "}
                  {q.course_name})
                </Text>
              ))}
            </Box>
            <Box direction="row" justify="end" gap="small">
              <Button label="Back" onClick={() => setShowSummary(false)} />
              <Button label="Confirm" primary onClick={handleConfirm} />
            </Box>
          </>
        )}
      </Box>
    </Layer>
  );
};

export default AddQuestionsModal;
